import React, { useCallback, useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { MdArrowBack, MdChevronLeft, MdChevronRight, MdOpenInNew } from "react-icons/md"
import { MobergLogoSpinner } from "../../Components/MobergLogoSpinner/MobergLogoSpinner"
import { useOpenOriginal } from "../../Components/Clara/originalPdf"
import { FRONTEND_LINKS } from "../../Constants/FrontendLinks"
import { useAuthProvider } from "../../Providers/AuthProvider"
import { useWorkspacesProvider } from "../../Providers/WorkspacesProvider"
import { listQuery } from "./claraDocumentsList"
import { ClaraDocumentPanel, ClaraQuestionTest, TypeBadge } from "./ClaraDocumentPanel"
import { usePdfUrl } from "./ClaraPdfPreview"
import {
	Banner, Center, Crumbs, DocMain, DocTab, DocTabs, Empty, IconButton, LinkButton, Muted, PageContainer, PdfBox, PdfBar, PreviewFrame,
	Split, StatusTag,
} from "./claraDocumentsStyles"

const CLARA_API = window.env?.REACT_APP_CLARA_API_URL
const DOCUMENTS = FRONTEND_LINKS.SUBPAGES.CLARA.SUBPAGES.DOCUMENTS.MAIN
const POLL_MS = 3000

export default function ClaraDocumentPage() {
	const { documentId } = useParams()
	const navigate = useNavigate()
	const location = useLocation()
	const { getToken } = useAuthProvider()
	const { selectedWorkspace } = useWorkspacesProvider()
	const openOriginal = useOpenOriginal()
	const [data, setData] = useState(null)
	const [error, setError] = useState(null)
	const [tab, setTab] = useState("pdf")
	const id = Number(documentId)
	const document = data?.forId === id ? data.documents?.[0] ?? null : null

	const load = useCallback(async () => {
		if (!selectedWorkspace || !CLARA_API) return
		try {
			const response = await fetch(`${CLARA_API}/documents?${listQuery(selectedWorkspace, { ids: [id], perPage: 1, counts: false })}`, { headers: { Authorization: getToken() } })
			const body = await response.json().catch(() => ({}))
			if (!response.ok) throw new Error(body.error || `status ${response.status}`)
			setData({ ...body, forId: id })
			setError(null)
		} catch (e) {
			setError(`Could not load the document: ${e.message}`)
			setData(d => (d?.forId === id ? d : { documents: [], failed: true, forId: id }))
		}
	}, [selectedWorkspace, id, getToken])

	useEffect(() => {
		setData(null)
		setError(null)
		setTab("pdf")
		load()
	}, [load])

	const indexing = ["pending", "indexing"].includes(document?.status)
	useEffect(() => {
		if (!indexing) return undefined
		const timer = setInterval(load, POLL_MS)
		return () => clearInterval(timer)
	}, [indexing, load])

	const { url: pdfUrl, error: pdfError } = usePdfUrl(document, selectedWorkspace, getToken)

	const ids = location.state?.ids ?? []
	const index = ids.indexOf(id)
	const go = target => navigate(`${DOCUMENTS}/${target}`, { state: { ids } })
	const back = () => navigate(DOCUMENTS)

	if (!CLARA_API) return <PageContainer><Banner $tone="error">Clara is not configured.</Banner></PageContainer>
	if (data === null) return <PageContainer><Center><MobergLogoSpinner /></Center></PageContainer>

	const canManage = data.can_manage === true
	const tabs = [["pdf", "PDF"], ...(canManage ? [["test", "Test a question"]] : [])]

	return (
		<PageContainer>
			<Crumbs>
				<IconButton type="button" onClick={back} aria-label="Back to Clara Documents" title="Back to Clara Documents" style={{ marginLeft: -8 }}>
					<MdArrowBack size={20} />
				</IconButton>
				<LinkButton type="button" onClick={back} style={{ alignSelf: "center", fontSize: 13 }}>Clara Documents</LinkButton>
				<Muted style={{ fontSize: 13 }}>/</Muted>
				<Muted style={{ fontSize: 13, flexGrow: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{document?.title ?? "Not found"}</Muted>
				{index >= 0 && (
					<>
						<Muted style={{ fontSize: 13 }}>{index + 1} of {ids.length}</Muted>
						<IconButton type="button" onClick={() => go(ids[index - 1])} disabled={index <= 0} aria-label="Previous document" title="Previous document" style={{ width: 30, height: 30 }}>
							<MdChevronLeft size={18} />
						</IconButton>
						<IconButton type="button" onClick={() => go(ids[index + 1])} disabled={index >= ids.length - 1} aria-label="Next document" title="Next document" style={{ width: 30, height: 30 }}>
							<MdChevronRight size={18} />
						</IconButton>
					</>
				)}
			</Crumbs>
			{error && <Banner $tone="error">{error}</Banner>}

			{!document ? (!data.failed && (
				<Empty>
					This document is not in this workspace. It may have been deleted.{" "}
					<LinkButton type="button" onClick={back} style={{ fontSize: 14 }}>Back to Clara Documents</LinkButton>
				</Empty>
			)) : (
				<Split>
					<DocMain>
						<h1>{document.title}</h1>
						<div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
							<TypeBadge document={document} plain />
							{document.review_status === "overdue" && <StatusTag $tone="danger" style={{ alignSelf: "center" }}>Review overdue</StatusTag>}
							{indexing && <StatusTag $tone="info" style={{ alignSelf: "center" }}>Being read</StatusTag>}
							{document.page_count != null && document.chunk_count != null && (
								<Muted style={{ fontSize: 13 }}>{document.page_count} pages, {document.chunk_count} passages Clara can quote</Muted>
							)}
						</div>
						<DocTabs aria-label="Document views">
							{tabs.map(([key, text]) => (
								<DocTab key={key} type="button" aria-pressed={tab === key} $active={tab === key} onClick={() => setTab(key)}>{text}</DocTab>
							))}
						</DocTabs>
						{tab === "pdf" && (
							<PdfBox>
								{document.has_original ? (
									<>
										<PdfBar>
											<span style={{ flexGrow: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{document.filename}</span>
											<LinkButton type="button" onClick={() => openOriginal(document.id, null)} style={{ alignSelf: "center", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}>
												Open in new tab <MdOpenInNew size={13} />
											</LinkButton>
										</PdfBar>
										{pdfUrl && <PreviewFrame src={pdfUrl} title={`${document.title} PDF`} />}
										{!pdfUrl && !pdfError && <Muted style={{ padding: 20 }}>Loading the PDF…</Muted>}
										{pdfError && <Muted style={{ padding: 20, color: "var(--color-danger-text-hover)" }}>Could not load the PDF: {pdfError}</Muted>}
									</>
								) : (
									<Empty>
										No PDF is stored for this document. Clara kept only the text it read, so it can quote the document but a citation cannot open the file.
										{canManage && data.originals_stored && " Use Attach PDF on the right to add the exact file that was uploaded."}
									</Empty>
								)}
							</PdfBox>
						)}
						{tab === "test" && <ClaraQuestionTest document={document} workspace={selectedWorkspace} getToken={getToken} onError={setError} />}
					</DocMain>
					<ClaraDocumentPanel
						document={document}
						canManage={canManage}
						originalsStored={data.originals_stored}
						workspace={selectedWorkspace}
						getToken={getToken}
						onChanged={load}
						onDeleted={back}
						onError={setError}
					/>
				</Split>
			)}
		</PageContainer>
	)
}
